/**
 * Reads the shift snapshot the edge renderer inlines into index.html
 * (window.__MB_JOBS__ / window.__MB_JOB__), so the jobs surface paints
 * before the first Supabase round trip. The live fetch still runs in the
 * background and reconciles — the snapshot is a head start, not a cache.
 */
import { isPublishableMarketplaceJob, type PublicJobPost } from "./job-types";

declare global {
  interface Window {
    __MB_JOBS__?: { fetchedAt: number; jobs: PublicJobPost[] };
    __MB_JOB__?: PublicJobPost;
  }
}

/** CDN may serve stale-while-revalidate HTML; ignore snapshots older than this. */
const MAX_SNAPSHOT_AGE_MS = 30 * 60 * 1000;

export function readJobsSnapshot(): PublicJobPost[] | null {
  try {
    const snap = window.__MB_JOBS__;
    if (!snap || !Array.isArray(snap.jobs)) return null;
    if (typeof snap.fetchedAt !== "number" || Date.now() - snap.fetchedAt > MAX_SNAPSHOT_AGE_MS) {
      return null;
    }
    const jobs = snap.jobs.filter(isPublishableMarketplaceJob);
    return jobs.length > 0 ? jobs : null;
  } catch {
    return null;
  }
}

/** Detail snapshot is only trusted for the exact page it was rendered for. */
export function readJobSnapshot(slug: string): PublicJobPost | null {
  try {
    const job = window.__MB_JOB__;
    if (!job || job.slug !== slug) return null;
    return isPublishableMarketplaceJob(job) ? job : null;
  } catch {
    return null;
  }
}
